// Admin API client

// Use relative path in dev (proxied), full URL in production
const API_BASE = import.meta.env.DEV ? '/api' : 'https://api.audiaro.com/v1';

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

class AdminApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('admin_token', token);
    } else {
      localStorage.removeItem('admin_token');
    }
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('admin_token');
    }
    return this.token;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const token = this.getToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers,
      });

      const data = await response.json() as ApiResponse<T>;

      if (!response.ok) {
        return {
          success: false,
          error: data.error || { code: 'ERROR', message: `Request failed with status ${response.status}` },
        };
      }

      return data;
    } catch (error) {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: error instanceof Error ? error.message : 'Network error' },
      };
    }
  }

  // Auth
  async login(email: string, password: string) {
    // Login endpoint returns data at root level, not under 'data' key
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const result = await response.json() as {
      success: boolean;
      user?: { id: string; email: string; role: string; first_name: string | null; last_name: string | null; tier_id: string };
      token?: string;
      error?: { code: string; message: string };
    };

    if (result.success && result.user && result.token) {
      return {
        success: true,
        data: { user: result.user, token: result.token },
      };
    }

    return {
      success: false,
      error: result.error || { code: 'ERROR', message: 'Login failed' },
    };
  }

  async getMe() {
    return this.request<{
      id: string;
      email: string;
      first_name: string | null;
      last_name: string | null;
      role: string;
    }>('/auth/me');
  }

  // Credentials
  async getCredentials() {
    return this.request<{
      credentials: Credential[];
      by_provider: Record<string, Credential[]>;
    }>('/admin/credentials');
  }

  async setCredential(keyName: string, value: string) {
    return this.request<{ message: string }>(`/admin/credentials/${keyName}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  }

  async clearCredential(keyName: string) {
    return this.request<{ message: string }>(`/admin/credentials/${keyName}`, {
      method: 'DELETE',
    });
  }

  async verifyProvider(provider: string) {
    return this.request<{ message: string }>(`/admin/credentials/${provider}/verify`, {
      method: 'POST',
    });
  }

  // Stats
  async getStats() {
    return this.request<{
      users: {
        total: number;
        active_subscriptions: number;
        by_tier: Array<{ name: string; count: number }>;
      };
      usage: {
        today: { characters: number; audio_files: number };
        this_month: { characters: number; audio_files: number };
      };
    }>('/admin/stats');
  }

  // Users
  async getUsers(params?: { limit?: number; offset?: number; search?: string }) {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    if (params?.search) query.set('search', params.search);

    return this.request<Array<{
      id: string;
      email: string;
      first_name: string | null;
      last_name: string | null;
      role: string;
      tier_id: string;
      tier_name: string;
      subscription_status: string | null;
      created_at: string;
    }>>(`/admin/users?${query.toString()}`);
  }

  async updateUser(userId: string, data: { role?: string; tier_id?: string }) {
    return this.request<{ message: string }>(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async resetUserPassword(userId: string, newPassword: string) {
    return this.request<{ message: string }>(`/admin/users/${userId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ new_password: newPassword }),
    });
  }

  // Tiers
  async getTiers() {
    return this.request<Array<{
      id: string;
      name: string;
      slug: string;
      monthly_characters: number;
      price_monthly: number;
      stripe_price_id_monthly: string | null;
      stripe_price_id_yearly: string | null;
      is_active: boolean;
    }>>('/admin/tiers');
  }

  async updateTier(tierId: string, data: Record<string, unknown>) {
    return this.request<{ message: string }>(`/admin/tiers/${tierId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Activity
  async getActivity(params?: { limit?: number }) {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', params.limit.toString());

    return this.request<Array<{
      id: string;
      admin_email: string;
      action: string;
      resource_type: string;
      resource_id: string | null;
      created_at: string;
    }>>(`/admin/activity?${query.toString()}`);
  }
}

export interface Credential {
  id: string;
  key_name: string;
  display_name: string;
  provider: string;
  description: string | null;
  is_required: boolean;
  is_configured: boolean;
  verification_status: 'pending' | 'valid' | 'invalid';
  last_verified_at: string | null;
  masked_value?: string;
  updated_at: string;
}

export const api = new AdminApiClient();
